import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests and prevent caching
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Prevent caching of API requests
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status } = error.response;

      // Handle authentication errors
      if (status === 401 || status === 403) {
        // Clear stored auth data
        localStorage.removeItem('token');

        // Redirect to login if not already there
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Helper to get token
const getToken = () => localStorage.getItem('token');

// Helper to clear auth and redirect (for use in components)
export const clearAuthAndRedirect = () => {
  localStorage.removeItem('token');
  window.location.href = '/login';
};

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
};

// User API
export const userAPI = {
  getMe: () => api.get('/users/me'),
  getCredits: () => api.get('/users/credits'),
};

// Payment API
export const paymentAPI = {
  // Get available subscription plans (public)
  getPlans: () => api.get('/payments/plans'),

  // Get current subscription status
  getSubscriptionStatus: () => api.get('/payments/subscription-status'),

  // Create checkout session for subscription
  createCheckoutSession: (plan) => api.post('/payments/create-checkout-session', { plan }),

  // Cancel current subscription
  cancelSubscription: () => api.post('/payments/cancel-subscription'),

  // Get billing portal URL for managing subscription
  getBillingPortal: () => api.post('/payments/billing-portal'),

  // Get payment history
  getPaymentHistory: () => api.get('/payments/history'),

  // Verify checkout session after redirect
  verifySession: (sessionId) => api.get(`/payments/verify-session/${sessionId}`),
};

// Screening API
export const screeningAPI = {
  create: (formData) => {
    return api.post('/screenings', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getAll: () => api.get('/screenings'),
  getById: (id) => api.get(`/screenings/${id}`),
};

// ============================================================================
// ENHANCED DOWNLOAD FUNCTIONS - USE THESE
// ============================================================================

/**
 * Download ZIP with actual resumes + ranking + analysis + CSV
 * This is the MAIN download function - includes everything HR needs
 */
export const downloadCandidatesWithResumes = async (screeningId, candidateIds) => {
  const token = getToken();

  const response = await fetch(
    `${API_URL}/screenings/${screeningId}/download-with-resumes`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ candidateIds })
    }
  );

  if (!response.ok) {
    throw new Error('Download failed');
  }

  // Get filename from header
  const contentDisposition = response.headers.get('Content-Disposition');
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1].replace(/"/g, '')
    : 'candidates_with_resumes.zip';

  // Download ZIP file
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * Download individual resume file (PDF/DOCX)
 * Use this for "Download Resume" button on individual cards
 */
export const downloadIndividualResume = async (resumeId) => {
  const token = getToken();

  const response = await fetch(`${API_URL}/resumes/${resumeId}/download`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) throw new Error('Failed to download resume');

  const contentDisposition = response.headers.get('Content-Disposition');
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1].replace(/"/g, '')
    : 'resume.pdf';

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export default api;